// ═══ 말소 홈·말소 탭 집계·필터·정렬 — 순수 함수 (B3 리뷰 §2.3 · 앱 malso_home_controller.dart 와 같은 정의) ═══
//   rows  = GET /cancel-requests (활성 업체, 차량별 최신 비취소 1건 — id 내림차순)
//   docs  = GET /cancel-doc-requests/my?status=요청 (소속 **전 업체** — rows 의 carId 교집합만 센다)
//   flags = GET /cancel-requests/malso-cert-flags 의 flags ({[id]: bool})

// 진행 중 = 대기·접수·진행중·수거완료 (완료·보류 제외).
var ACTIVE = { '대기': 1, '접수': 1, '진행중': 1, '수거완료': 1 };

export function isActive(r) { return !!ACTIVE[r && r.status]; }

/// 행 날짜 'YYYY-MM-DD' — 완료 건은 완료일, 그 외 신청 시각(createdAt)의 **브라우저 로컬** 날짜.
/// requestDate 는 서버가 UTC 날짜로 저장해 한국 00~09시 신청이 하루 앞서므로 createdAt 이 없을 때만 쓴다.
/// 목록 표시·기간(월) 필터·정렬이 같은 기준이라 홈 '이번 달 완료' 와 말소 탭 '완료 + 이번 달' 이 같은 건을
/// 가리킨다(Jin 10/27, 앱 MalsoHomeData.rowDate = completedDate ?? createdAt(로컬) ?? requestDate).
export function rowDate(r) {
  if (!r) return '';
  if (r.status === '완료' && r.completedDate) return String(r.completedDate).slice(0, 10);
  var c = r.createdAt ? new Date(r.createdAt) : null;
  if (c && !isNaN(c.getTime())) {
    var mm = c.getMonth() + 1, dd = c.getDate();
    return c.getFullYear() + '-' + (mm < 10 ? '0' : '') + mm + '-' + (dd < 10 ? '0' : '') + dd;
  }
  return String(r.requestDate || '').slice(0, 10);
}

/// 행 날짜의 'YYYY-MM' (날짜가 없으면 '').
export function rowYm(r) {
  var d = rowDate(r);
  return /^\d{4}-\d{2}/.test(d) ? d.slice(0, 7) : '';
}

/// 말소증 플래그 조회 대상 — '완료' 행 id 만, completedDate 내림차순 ≤200(서버 상한).
export function certIdsOf(rows) {
  return (rows || []).filter(function (r) { return r.status === '완료'; })
    .sort(function (a, b) {
      var da = String(a.completedDate || ''), db = String(b.completedDate || '');
      return da === db ? 0 : (da < db ? 1 : -1);
    })
    .slice(0, 200)
    .map(function (r) { return r.id; });
}

/// 화면 데이터 1벌 — 칩 카운트·홈 3칸·노란 띠 건수. month = 'YYYY-MM'(fmt.currentMonth).
/// rows 는 행 날짜 최신순(같은 날은 서버 순서 = id 내림차순 — sort 는 안정 정렬).
export function buildHome(rows, docs, flags, month) {
  var list = (Array.isArray(rows) ? rows : []).slice().sort(function (a, b) {
    var da = rowDate(a), db = rowDate(b);
    return da === db ? 0 : (da < db ? 1 : -1);
  });
  var carIds = {};
  list.forEach(function (r) { carIds[r.carId] = 1; });
  var needCars = {};
  var docCount = 0;
  (Array.isArray(docs) ? docs : []).forEach(function (d) {
    if (!d || d.status !== '요청' || !carIds[d.carId]) return;
    needCars[d.carId] = 1;
    docCount += 1;
  });
  var home = { rows: list, needCars: needCars, flags: flags || {}, docCount: docCount };
  home.counts = countsOf(home, '');
  home.doneMonth = countsOf(home, month).done;
  return home;
}

/// 기간 안 행 — ym '' = 전체 기간.
function inPeriod(home, ym) {
  var list = home ? home.rows : [];
  return ym ? list.filter(function (r) { return rowYm(r) === ym; }) : list;
}

/// 칩 4개 건수 — ym '' = 전체 기간.
export function countsOf(home, ym) {
  var list = inPeriod(home, ym);
  return {
    all: list.length,
    active: list.filter(isActive).length,
    needsAction: list.filter(function (r) { return needsAction(home, r); }).length,
    done: list.filter(function (r) { return r.status === '완료'; }).length
  };
}

/// 기간 선택 목록 — [{ ym, count }] 최신 달 먼저.
export function monthsOf(home) {
  var by = {};
  (home ? home.rows : []).forEach(function (r) { var ym = rowYm(r); if (ym) by[ym] = (by[ym] || 0) + 1; });
  return Object.keys(by).sort().reverse().map(function (ym) { return { ym: ym, count: by[ym] }; });
}

/// 확인 필요 = 보류 ∪ 미제출 보강서류가 걸린 차량.
export function needsAction(home, r) { return r.status === '보류' || !!home.needCars[r.carId]; }

export function certArrived(home, r) { return r.status === '완료' && !!home.flags[r.id]; }

/// 칩 필터 + 기간(ym '' = 전체). '전체' 는 확인 필요 → 진행 중 → 완료 순(그룹 안은 행 날짜 최신순 —
/// SimpleRequests.visibleOrders 와 같은 규칙).
export function visibleRows(home, filter, ym) {
  var list = inPeriod(home, ym);
  if (filter === 'active') return list.filter(isActive);
  if (filter === 'needsAction') return list.filter(function (r) { return needsAction(home, r); });
  if (filter === 'done') return list.filter(function (r) { return r.status === '완료'; });
  var first = [], mid = [], last = [];
  list.forEach(function (r) { (needsAction(home, r) ? first : r.status === '완료' ? last : mid).push(r); });
  return first.concat(mid, last);
}
